import React from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import InfoIcon from '@mui/icons-material/Info';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

interface ChartPanelCardProps {
  title: React.ReactNode;
  info: React.ReactNode;
  renderContent: (isExpanded: boolean) => React.ReactNode;
  paperSx?: SxProps<Theme>;
  dialogPaperSx?: SxProps<Theme>;
  dialogContentSx?: SxProps<Theme>;
}

const ChartPanelCard: React.FC<ChartPanelCardProps> = ({
  title,
  info,
  renderContent,
  paperSx,
  dialogPaperSx,
  dialogContentSx,
}) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [isMaximized, setIsMaximized] = React.useState(true);

  const handleOpen = () => {
    setIsMaximized(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleToggleMaximize = () => {
    setIsMaximized((current) => !current);
  };

  const alert = (
    <Alert
      icon={<InfoIcon fontSize="inherit" />}
      severity="info"
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
    >
      <Typography variant="body2" component="span">
        {info}
      </Typography>
    </Alert>
  );

  return (
    <>
      <Paper
        variant="outlined"
        sx={paperSx ? ([{ p: 2 }, paperSx] as SxProps<Theme>) : { p: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
          <Tooltip title="Maximize chart">
            <IconButton
              onClick={handleOpen}
              aria-label="Maximize chart"
              size="small"
              sx={{ flexShrink: 0 }}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {alert}
        {renderContent(false)}
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isMaximized ? false : 'lg'}
        fullWidth
        PaperProps={{
          sx: dialogPaperSx
            ? [
                {
                  display: 'flex',
                  flexDirection: 'column',
                  width: isMaximized ? '100vw' : undefined,
                  maxWidth: isMaximized ? '100vw' : 1400,
                  height: isMaximized ? '100vh' : '92vh',
                  maxHeight: '100vh',
                  borderRadius: isMaximized ? 0 : 2,
                  m: isMaximized ? 0 : 2,
                },
                dialogPaperSx,
              ] as SxProps<Theme>
            : {
                display: 'flex',
                flexDirection: 'column',
                width: isMaximized ? '100vw' : undefined,
                maxWidth: isMaximized ? '100vw' : 1400,
                height: isMaximized ? '100vh' : '92vh',
                maxHeight: '100vh',
                borderRadius: isMaximized ? 0 : 2,
                m: isMaximized ? 0 : 2,
              },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: 'common.white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 2,
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={isMaximized ? 'Restore size' : 'Maximize dialog'}>
              <IconButton
                onClick={handleToggleMaximize}
                aria-label={isMaximized ? 'Restore dialog size' : 'Maximize dialog'}
                sx={{
                  color: 'common.white',
                  '&:hover': {
                    backgroundColor: alpha('#ffffff', 0.1),
                  },
                }}
              >
                {isMaximized ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Close">
              <IconButton
                onClick={handleClose}
                aria-label="Close dialog"
                sx={{
                  color: 'common.white',
                  '&:hover': {
                    backgroundColor: alpha('#ffffff', 0.1),
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>

        <DialogContent
          dividers
          sx={
            dialogContentSx
              ? [
                  {
                    p: { xs: 2, md: 3 },
                    overflowY: 'auto',
                  },
                  dialogContentSx,
                ] as SxProps<Theme>
              : {
                  p: { xs: 2, md: 3 },
                  overflowY: 'auto',
                }
          }
        >
          {alert}
          {renderContent(true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChartPanelCard;
